import React, {useState, forwardRef, useCallback, useEffect, useRef} from 'react'
import {Stack, Box, Checkbox, TextInput, Button, Flex, Text, Label} from '@sanity/ui'
import {set, StringInputProps, unset} from 'sanity'
import {TrashIcon, AddIcon} from '@sanity/icons'

interface AnchorItem {
  url: string
  label: string
  active: boolean
  isNew?: boolean
  saved?: boolean
}

interface FitmentAnchorsProps extends StringInputProps {}

const FitmentAnchors = forwardRef<HTMLDivElement, FitmentAnchorsProps>((props, ref) => {
  const {value, onChange, schemaType} = props

  const defaultAnchors: AnchorItem[] = [
    {url: '#oem', label: 'OEM+', active: true},
    {url: '#pst', label: 'Performance Street & Track', active: true},
    {url: '#track', label: 'Hardcore Track', active: true},
    {url: '#race', label: 'Race', active: true},
    {url: '#spec', label: 'Spec Race', active: true},
    {url: '#stance', label: 'Stance', active: true},
    {url: '#winter', label: 'Winter', active: true},
  ]

  const checkCustomAnchors = (anchor: AnchorItem): AnchorItem => {
    const defaultUrls = defaultAnchors.map(({url}) => url)
    if (!defaultUrls.includes(anchor.url)) {
      return {...anchor, saved: true}
    }
    return anchor
  }

  const initializeAnchors = (): AnchorItem[] => {
    if (!value) return defaultAnchors

    try {
      const parsed: AnchorItem[] = JSON.parse(value).map(checkCustomAnchors)
      const missingDefaults = defaultAnchors.filter(
        ({url}) => !parsed.map(({url}) => url).includes(url),
      )
      return [...missingDefaults, ...parsed]
    } catch (error) {
      console.error('Error parsing fitment anchors value:', error)
      return defaultAnchors
    }
  }

  const [anchors, setAnchors] = useState<AnchorItem[]>(initializeAnchors())
  const [newAnchor, setNewAnchor] = useState<AnchorItem | false>(false)

  const newAnchorLabelRef = useRef<HTMLInputElement>(null)
  const newAnchorUrlRef = useRef<HTMLInputElement>(null)

  const updateAnchorState = (updatedAnchors: AnchorItem[]) => {
    setAnchors(updatedAnchors)
    onChange(updatedAnchors.length > 0 ? set(JSON.stringify(updatedAnchors)) : unset())
  }

  const handleCheckboxChange = useCallback(
    (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const updatedAnchors = [...anchors]
      updatedAnchors[index].active = event.target.checked
      updateAnchorState(updatedAnchors)
    },
    [anchors, onChange],
  )

  const createNewAnchor = () => {
    setAnchors([...anchors, {url: '#', label: '', active: true, isNew: true}])
  }

  const cancelNewAnchor = () => {
    setAnchors(anchors.filter(({isNew = false}) => !isNew))
  }

  const saveNewAnchor = () => {
    if (!newAnchorLabelRef.current || !newAnchorUrlRef.current) return

    const label = newAnchorLabelRef.current.value.trim()
    const url = newAnchorUrlRef.current.value.trim()

    if (!label || !url) return

    const strippedAnchors = anchors.filter(({isNew = false}) => !isNew)
    const updatedAnchors = [...strippedAnchors, {url, label, active: true, saved: true}]
    updateAnchorState(updatedAnchors)
  }

  const removeAnchor = (index: number) => {
    const updatedAnchors = anchors.filter((_, i) => i !== index)
    updateAnchorState(updatedAnchors)
  }

  useEffect(() => {
    const foundNewAnchor = anchors.find(({isNew = false}) => isNew) || false
    setNewAnchor(foundNewAnchor)

    if (foundNewAnchor && newAnchorLabelRef.current) {
      newAnchorLabelRef.current.focus()
    }
  }, [anchors])

  return (
    <Stack gap={3} ref={ref}>
      <Box>
        <Label size={1} weight="medium">
          {schemaType.title || 'Fitment Anchors'}
        </Label>
        {schemaType.description && (
          <Text size={1} muted style={{marginTop: 4}}>
            {schemaType.description}
          </Text>
        )}
      </Box>

      <Stack gap={2}>
        {anchors.map((anchor, index) => (
          <Box key={index} padding={2} style={{border: '1px solid #e1e3e6', borderRadius: 4}}>
            {anchor.isNew ? (
              <Stack gap={3}>
                <Flex align="center" gap={2}>
                  <Checkbox checked={anchor.active} disabled />
                  <Text weight="medium">New Anchor</Text>
                </Flex>

                <Stack gap={2}>
                  <Box>
                    <Label size={1}>Label</Label>
                    <TextInput ref={newAnchorLabelRef} placeholder="Enter anchor label" />
                  </Box>
                  <Box>
                    <Label size={1}>Anchor URL</Label>
                    <TextInput ref={newAnchorUrlRef} placeholder="#anchor-url" />
                  </Box>
                </Stack>

                <Flex gap={2}>
                  <Button text="Save" tone="positive" onClick={saveNewAnchor} />
                  <Button
                    text="Cancel"
                    tone="critical"
                    mode="ghost"
                    onClick={cancelNewAnchor}
                   
                  />
                </Flex>
              </Stack>
            ) : (
              <Flex align="center" justify="space-between">
                <Flex align="center" gap={2} flex={1}>
                  <Checkbox checked={anchor.active} onChange={handleCheckboxChange(index)} />
                  <Text>
                    {anchor.label} ({anchor.url})
                  </Text>
                </Flex>

                {anchor.saved && (
                  <Button
                    icon={TrashIcon}
                    mode="ghost"
                    tone="critical"
                    onClick={() => removeAnchor(index)}
                    title="Delete anchor"
                   
                  />
                )}
              </Flex>
            )}
          </Box>
        ))}

        {!newAnchor && (
          <Box>
            <Button
              icon={AddIcon}
              text="Add New Anchor"
              mode="ghost"
              onClick={createNewAnchor}
             
            />
          </Box>
        )}
      </Stack>
    </Stack>
  )
})

FitmentAnchors.displayName = 'FitmentAnchors'

export default FitmentAnchors
